export interface FirmanteColorTokens {
  border: string;
  background: string;
  accent: string;
}

const DEFAULT_TOKENS: FirmanteColorTokens = {
  border: '#D4D4D4',
  background: '#FAFAFA',
  accent: '#9E9E9E',
};

const BY_CODIGO: Record<string, FirmanteColorTokens> = {
  '100': { border: '#E891A8', background: '#FEF5F7', accent: '#F8B4C4' },
  '101': { border: '#7BC47B', background: '#F3FBF3', accent: '#B8E6B8' },
  '102': { border: '#7BA8E8', background: '#F3F7FB', accent: '#B4D4F8' },
};

const ADICIONAL_PALETTE: FirmanteColorTokens[] = [
  { border: '#C9A8E8', background: '#F9F5FE', accent: '#D4B8F8' },
  { border: '#E8C47B', background: '#FEFAF3', accent: '#E8D4B8' },
  { border: '#7BC4C4', background: '#F3FBFB', accent: '#B8E6E6' },
  { border: '#E8A05C', background: '#FEF6EF', accent: '#F5C9A0' },
  { border: '#E87B7B', background: '#FEF3F3', accent: '#F5B4B4' },
  { border: '#7B8AE8', background: '#F3F4FB', accent: '#B4BEF8' },
  { border: '#A8C47B', background: '#F7FBF3', accent: '#D4E8B8' },
  { border: '#E87BC4', background: '#FEF3F9', accent: '#F5B4DC' },
  { border: '#8A9EB0', background: '#F5F7F9', accent: '#B8C8D4' },
  { border: '#C49A7B', background: '#FBF6F3', accent: '#E0C4B0' },
];

const ADICIONAL_CODIGO_MIN = 103;
const ADICIONAL_CODIGO_MAX = 112;

const CASILLA_FILL_ALPHA = 0.4;
const CASILLA_HIGHLIGHT_FILL_ALPHA = 0.98;

export function getFirmanteColorTokens(codigo: string): FirmanteColorTokens {
  if (BY_CODIGO[codigo]) {
    return BY_CODIGO[codigo];
  }
  const n = Number(codigo);
  if (n >= ADICIONAL_CODIGO_MIN && n <= ADICIONAL_CODIGO_MAX) {
    return ADICIONAL_PALETTE[n - ADICIONAL_CODIGO_MIN];
  }
  return DEFAULT_TOKENS;
}

export function firmanteColorCssVars(codigo: string): Record<string, string> {
  const t = getFirmanteColorTokens(codigo);
  return {
    '--firmante-border': t.border,
    '--firmante-bg': t.background,
    '--firmante-accent': t.accent,
  };
}

function hexToRgba(hex: string, alpha: number): string {
  const h = hex.replace('#', '');
  const r = parseInt(h.substring(0, 2), 16);
  const g = parseInt(h.substring(2, 4), 16);
  const b = parseInt(h.substring(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

/** Estilo naranja legacy (pasos sin color por firmante). */
export function legacyOverlayStyle(): Record<string, string> {
  return {
    'background-color': 'rgba(244, 114, 36, 0.4)',
    'border-width': '2px',
    'border-color': 'var(--accent)',
    '--overlay-border-color': 'var(--accent)',
  };
}

/** Estilos inline para overlays PDF y fantasmas de arrastre (firma, campo). El estilo de trazo va por clase CSS. */
export function firmanteOverlayStyle(codigo: string | undefined): Record<string, string> {
  if (!codigo) {
    return legacyOverlayStyle();
  }
  const t = getFirmanteColorTokens(codigo);
  return {
    'background-color': hexToRgba(t.accent, CASILLA_FILL_ALPHA),
    'border-width': '2px',
    'border-color': t.border,
    '--overlay-border-color': t.border,
  };
}

/**
 * Firma: debe leerse claramente con el color del firmante (mismo tono que su tarjeta).
 * Usa el color principal del firmante (`border`) tanto en el borde como en un relleno
 * mas saturado que el de casillas/campos, para que se identifique de un vistazo.
 */
export function firmaOverlayStyle(codigo: string | undefined): Record<string, string> {
  if (!codigo) {
    return legacyOverlayStyle();
  }
  const t = getFirmanteColorTokens(codigo);
  return {
    'background-color': hexToRgba(t.border, 0.22),
    'border-width': '2px',
    'border-color': t.border,
    '--overlay-border-color': t.border,
  };
}

/** Casillas: mismo color base que firma/campo; mas oscuro si el formulario esta seleccionado en el panel. */
export function casillaOverlayStyle(
  codigo: string | undefined,
  highlighted = false
): Record<string, string> {
  if (!codigo) {
    return legacyOverlayStyle();
  }
  const t = getFirmanteColorTokens(codigo);
  return {
    'background-color': hexToRgba(t.accent, highlighted ? CASILLA_HIGHLIGHT_FILL_ALPHA : CASILLA_FILL_ALPHA),
    'border-width': '2px',
    'border-color': t.border,
    '--overlay-border-color': t.border,
  };
}

/** Pastilla de color en panel lateral (misma tinta que el PDF para el formulario). */
export function casillaFillSwatchStyle(
  codigo: string | undefined,
  highlighted = false
): Record<string, string> {
  if (!codigo) {
    return { 'background-color': 'rgba(244, 114, 36, 0.4)' };
  }
  const t = getFirmanteColorTokens(codigo);
  return {
    'background-color': hexToRgba(t.accent, highlighted ? CASILLA_HIGHLIGHT_FILL_ALPHA : CASILLA_FILL_ALPHA),
    'border-color': t.border,
  };
}

export function resolveOverlayStyle(overlayStyle: Record<string, string>): Record<string, string> {
  return Object.keys(overlayStyle).length > 0 ? overlayStyle : legacyOverlayStyle();
}
